import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    AlertCircle,
    ArrowLeft,
    CalendarDays,
    ImageOff,
    Loader2,
    Tag,
} from "lucide-react";
import { fetchPublicBlog, formatBlogDate } from "../lib/publicBlogs";

const BlogDetail = () => {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [imageError, setImageError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const controller = new AbortController();

        const loadBlog = async () => {
            setLoading(true);
            setError("");
            setImageError(false);

            try {
                const result = await fetchPublicBlog(slug, controller.signal);
                setBlog(result);
            } catch (err) {
                if (err.name === "AbortError") return;
                setBlog(null);
                setError("This blog is temporarily unavailable.");
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        loadBlog();

        return () => controller.abort();
    }, [slug]);

    const contentBlocks = useMemo(() => {
        const content = blog?.content || blog?.excerpt || "";
        return content
            .replace(/<[^>]*>/g, "")
            .split(/\n{2,}|\r\n{2,}/)
            .map((block) => block.trim())
            .filter(Boolean);
    }, [blog]);

    return (
        <section className="section bg-white" data-testid="blog-detail-section">
            <div className="container-vil max-w-5xl">
                <Link to="/blogs" className="btn-secondary mb-8 !py-2.5" data-testid="blog-back-link">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Blogs
                </Link>

                {loading && (
                    <div className="flex items-center justify-center gap-3 py-24 text-slate-500">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Loading blog
                    </div>
                )}

                {!loading && error && (
                    <div
                        className="flex items-start gap-3 border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                        data-testid="blog-detail-error"
                    >
                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                {!loading && blog && (
                    <article>
                        <div className="mb-8">
                            <div className="flex flex-wrap gap-3 text-xs font-mono uppercase tracking-widest text-slate-500 mb-5">
                                <span className="inline-flex items-center gap-2">
                                    <Tag className="w-4 h-4 text-[#E53935]" />
                                    {blog.categoryName}
                                </span>
                                <span className="inline-flex items-center gap-2">
                                    <CalendarDays className="w-4 h-4 text-[#0E4DA4]" />
                                    {formatBlogDate(blog.publishedAt) || "Latest insight"}
                                </span>
                            </div>

                            <h1 className="font-display font-black text-slate-900 text-4xl md:text-6xl leading-tight max-w-4xl">
                                {blog.title}
                            </h1>
                            <p className="text-slate-600 mt-5 leading-relaxed text-lg max-w-3xl">
                                {blog.excerpt}
                            </p>
                        </div>

                        <div className="relative overflow-hidden bg-slate-100 border border-slate-200 mb-10 h-64 md:h-80 lg:h-[360px]">
                            {blog.image && !imageError ? (
                                <img
                                    src={blog.image}
                                    alt={blog.title}
                                    className="w-full h-full object-cover"
                                    loading="eager"
                                    onError={() => setImageError(true)}
                                />
                            ) : (
                                <div className="h-full w-full flex flex-col items-center justify-center gap-2 text-slate-400">
                                    <ImageOff className="w-9 h-9" />
                                    <span className="text-xs font-mono uppercase tracking-widest">
                                        Image unavailable
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="max-w-3xl">
                            {contentBlocks.length > 0 ? (
                                <div className="space-y-5">
                                    {contentBlocks.map((block, index) => (
                                        <p
                                            key={`${blog.id}-content-${index}`}
                                            className="text-slate-700 leading-8 text-base md:text-lg"
                                        >
                                            {block}
                                        </p>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-slate-500">Blog content is being updated.</p>
                            )}
                        </div>
                    </article>
                )}
            </div>
        </section>
    );
};

export default BlogDetail;
